import type React from "react";
import {
  type ChangeEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import type { CountryCode } from "../../../types/common";
import { cn } from "../../../utils";
import { orgNumValidator } from "../../../utils/orgNumValidator";
import { TextInput } from "../TextInput";
import { type ICompanySearchItem, useCompanySearch } from "./useCompanySearch";

export interface IOrganisationFormInput {
  name: string;
  organisationNumber: string;
}

interface IOrganisationInputProps {
  value: string;
  countryCode: CountryCode;
  backendBaseUrl: string;
  touched?: boolean;
  error?: string;
  label?: ReactNode;
  placeholder?: string;
  required?: boolean;
  /** Input id; defaults to a generated unique id. */
  id?: string;
  isLimitedCompany: boolean;
  onCompanyChange: (organisation: IOrganisationFormInput) => void;
  onBlur: () => void;
  /** `data-test-id` on the organisation text input. */
  dataTestId?: string;
  /** `data-test-id` on each company search result button (integration tests). */
  companyResultTestId?: (index: number) => string;
}

export const OrganisationInput = ({
  value,
  countryCode,
  backendBaseUrl,
  touched,
  error,
  label,
  placeholder,
  required,
  id,
  isLimitedCompany,
  onCompanyChange,
  onBlur,
  dataTestId,
  companyResultTestId,
}: IOrganisationInputProps) => {
  const { t } = useTranslation("formFields");
  const generatedId = useId();

  const { data, isFetching } = useCompanySearch(
    backendBaseUrl,
    countryCode,
    value,
  );
  const { results, hasDuplicates } = data ?? {};

  const matchedCompany = results?.find(
    (it) => it.organisationNumber === value.replace(/-/g, ""),
  );
  const prevMatchedCompanyRef = useRef<ICompanySearchItem | undefined>(
    undefined,
  );

  const [isFocusedOnSearch, setIsFocusedOnSearch] = useState(false);

  const companyResultsItems = useRef<HTMLDivElement[]>([]);
  const companyInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const handleSearchFocusOrBlur = useCallback((e: MouseEvent) => {
    const focused =
      (companyInputRef.current?.contains(e.target as Node) ?? false) ||
      (resultsRef.current?.contains(e.target as Node) ?? false);
    setIsFocusedOnSearch(focused);
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleSearchFocusOrBlur);
    return () => document.removeEventListener("click", handleSearchFocusOrBlur);
  }, [handleSearchFocusOrBlur]);

  const formatOrgNum = useCallback(
    (orgNum: string) => {
      if (!orgNum) return orgNum;
      const result = orgNumValidator(orgNum, countryCode);
      if (!result.isValid) return orgNum;
      return countryCode === "SE" ? result.compact : result.formatted;
    },
    [countryCode],
  );

  const selectCompany = useCallback(
    (company: ICompanySearchItem) => {
      onCompanyChange({
        name: company.companyName,
        organisationNumber: formatOrgNum(company.organisationNumber),
      });
    },
    [onCompanyChange, formatOrgNum],
  );

  const onChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatOrgNum(target.value ?? "");
    onCompanyChange({ name: formatted, organisationNumber: formatted });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: `value` omitted so we do not run on every keystroke; see comment inside effect
  useEffect(() => {
    const compactValue = value.replace(/-/g, "");
    if (
      prevMatchedCompanyRef.current !== matchedCompany &&
      matchedCompany?.organisationNumber === compactValue
    ) {
      selectCompany(matchedCompany);
    }
    prevMatchedCompanyRef.current = matchedCompany;
  }, [selectCompany, matchedCompany]);

  const onCompanyReset = () => {
    setIsFocusedOnSearch(true);
    onCompanyChange({ name: "", organisationNumber: "" });
    setTimeout(() => companyInputRef.current?.focus(), 0);
  };

  const onCompanyInputKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const first = companyResultsItems.current[0];
    switch (e.key) {
      case "Tab":
        if (e.shiftKey || !first) setIsFocusedOnSearch(false);
        break;
      case "ArrowDown":
        e.preventDefault();
        first?.focus();
        break;
    }
  };

  const onCompanyItemKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const index = companyResultsItems.current.indexOf(
      e.target as unknown as HTMLDivElement,
    );
    const len = results?.length ?? 0;

    switch (e.key) {
      case "ArrowDown":
        if (len > index + 1) {
          e.preventDefault();
          companyResultsItems.current[index + 1]?.focus();
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (index === 0) {
          companyInputRef.current?.focus();
        } else {
          companyResultsItems.current[index - 1]?.focus();
        }
        break;
      case "Enter":
        if (results?.[index]) selectCompany(results[index]);
        break;
    }
  };

  const companyPlaceholder =
    (
      t("formFields.companyPlaceholder", { returnObjects: true }) as Record<
        string,
        string
      >
    )[countryCode] ?? "";

  const displayValue =
    matchedCompany && value
      ? `${matchedCompany.companyName} (${orgNumValidator(matchedCompany.organisationNumber, countryCode).formatted})`
      : value || "";

  const limitedCompanyConfirmation = isLimitedCompany
    ? t("formFields.limitedCompanyConfirmation")
    : undefined;

  return (
    <TextInput
      ref={companyInputRef}
      tabIndex={0}
      type="text"
      name="organisationNumber"
      id={id ?? generatedId}
      dataTestId={dataTestId}
      label={label ?? t("formFields.companyLabel")}
      placeholder={placeholder ?? companyPlaceholder}
      required={required}
      autoComplete="off"
      value={displayValue}
      readOnly={!!matchedCompany}
      error={!isFocusedOnSearch && touched && error ? error : undefined}
      confirmation={limitedCompanyConfirmation}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={() => setIsFocusedOnSearch(true)}
      onKeyDown={onCompanyInputKeyDown}
      textAppend={
        matchedCompany ? (
          <button
            type="button"
            className="z-50 mr-2 cursor-pointer rounded-sm bg-primary-150 px-2 py-1 text-sm font-semibold text-primary"
            onClick={onCompanyReset}
            aria-label={t("formFields.companyChange")}
          >
            {t("formFields.companyChange")}
          </button>
        ) : undefined
      }
      autoselect={
        isFocusedOnSearch && value && value.length > 0 && !matchedCompany ? (
          <div
            className="absolute top-full z-20 mt-4 w-full cursor-pointer overflow-hidden rounded-sm border bg-white shadow-md"
            ref={resultsRef}
          >
            <div
              role="listbox"
              tabIndex={0}
              className="m-0 max-h-[152px] list-none overflow-x-hidden overflow-y-scroll p-0 text-left"
            >
              {results?.map((r, index) => (
                <button
                  key={`${r.companyName}-${index}`}
                  type="button"
                  className="relative flex w-full cursor-pointer items-center justify-between gap-2 overflow-hidden bg-white px-4 py-2 text-left outline-hidden hover:bg-primary hover:text-white focus:bg-primary focus:text-white"
                  onClick={() => selectCompany(r)}
                  onKeyDown={onCompanyItemKeyDown}
                  {...(companyResultTestId
                    ? { "data-test-id": companyResultTestId(index) }
                    : {})}
                  ref={(el) => {
                    if (el)
                      companyResultsItems.current[index] =
                        el as unknown as HTMLDivElement;
                  }}
                >
                  {/* typography: search result item — hover state fully recolors via parent button, preset would force a fixed color */}
                  <div className="min-w-0 flex-1 truncate pr-2 text-base font-semibold leading-6">
                    {r.companyName}
                  </div>
                  {/* typography: chip turns brand-colored on parent hover — preset would force a fixed color */}
                  <span className="shrink-0 rounded-sm bg-white px-2 py-1 text-sm font-semibold text-gray-500 hover:text-primary">
                    {t("formFields.companySelect")}
                  </span>
                </button>
              ))}
            </div>
            {isFetching && (
              <div className="p-3 text-center text-base-strong">
                {t("formFields.companyPreloader")}.
              </div>
            )}
            {!isFetching && (
              <div
                className={cn(
                  "border-t border-gray-300 bg-white p-3 text-center text-base-strong transition duration-300 ease-in-out",
                  hasDuplicates && "border-orange bg-orange text-black",
                )}
              >
                {t("formFields.companyFindHelp")}
              </div>
            )}
          </div>
        ) : undefined
      }
    />
  );
};
